import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatDateTime } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Bell, CheckCheck, Info, AlertTriangle, CheckCircle, XCircle,
  ExternalLink
} from 'lucide-react';

const notificationIcons = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle,
  danger: XCircle,
};

const notificationColors = {
  info: 'bg-primary-100 dark:bg-primary-950/50 text-primary-900 dark:text-primary-400',
  warning: 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400',
  success: 'bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400',
  danger: 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-400',
};

const notificationBg = {
  info: 'hover:bg-primary-50 dark:hover:bg-primary-950/30',
  warning: 'hover:bg-warning-50 dark:hover:bg-warning-900/20',
  success: 'hover:bg-success-50 dark:hover:bg-success-900/20',
  danger: 'hover:bg-danger-50 dark:hover:bg-danger-900/20',
};

function NotificationItem({ notification, onMarkRead }) {
  const Icon = notificationIcons[notification.type] || Info;

  return (
    <div
      className={`px-6 py-4 flex items-start gap-4 transition-colors cursor-pointer
        ${!notification.is_read ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''}
        ${notificationBg[notification.type] || 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
      onClick={() => !notification.is_read && onMarkRead(notification.id)}
    >
      <div className={`p-2 rounded-full shrink-0 ${notificationColors[notification.type] || notificationColors.info}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm ${!notification.is_read ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}`}>
            {notification.title || notification.message}
          </p>
          {!notification.is_read && (
            <span className="shrink-0 w-2 h-2 rounded-full bg-primary-500 mt-1.5" />
          )}
        </div>
        {notification.title && notification.message !== notification.title && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{notification.message}</p>
        )}
        <div className="flex items-center mt-1 text-xs text-gray-400 dark:text-gray-500">
          <span>{formatDateTime(notification.created_at)}</span>
          {notification.link && (
            <a href={notification.link} className="ml-3 text-primary-500 hover:text-primary-800 flex items-center">
              <ExternalLink className="h-3 w-3 mr-1" /> Lihat
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setUpdating] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      if (res.data.success) {
        setNotifications(res.data.data || []);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    setUpdating(id);
    try {
      const res = await api.put(`/notifications/${id}/read`);
      if (res.data.success) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
        );
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setUpdating(null);
    }
  };

  const markAllAsRead = async () => {
    try {
      const res = await api.put('/notifications/read-all');
      if (res.data.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        toast.success('Semua notifikasi telah ditandai dibaca');
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Notifikasi</h1>
          <p className="page-subtitle">
            {unreadCount > 0
              ? `Anda memiliki ${unreadCount} notifikasi belum dibaca`
              : 'Tidak ada notifikasi baru'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllAsRead} className="btn-secondary btn-sm">
            <CheckCheck className="h-4 w-4 mr-1" /> Tandai Semua Dibaca
          </button>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-4">
                  <div className="skeleton h-10 w-10 rounded-full shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-4 w-3/4" />
                    <div className="skeleton h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Bell className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Notifikasi</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Belum ada notifikasi untuk saat ini. Notifikasi akan muncul ketika ada aktivitas penting.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onMarkRead={markAsRead} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}